import { useEffect, useMemo, useRef, useCallback } from 'react';
import { debounce } from 'lodash';
import serverEvents, { EventType } from '@/lib/sse/ServerEvents';
import {
    invalidateFetcherJsonByFilename,
    invalidateFetcherJsonByUrl,
    invalidateAllFetcherApiByPath,
} from '@/lib/query/useQueryFetcher';
import useIsMemberAuthorized from '@/hooks/useIsMemberAuthorized';

type ApiPathConfig = {
    /** API path to invalidate (e.g., 'positions') */
    path: string;
    /** File patterns that trigger this API path invalidation */
    filePatterns: RegExp[];
};

type FileInvalidationConfig = {
    /** Exact filenames to watch (e.g., 'builders_24h.json') */
    filenames?: string[];
    /** Regex patterns against filename (e.g., /^builders_(24h|7d|30d)\.json$/) */
    patterns?: RegExp[];
    /** If your SSE may send full URLs instead of filenames */
    urls?: string[];
    /** API paths to invalidate based on file patterns (only for authenticated users) */
    apiPaths?: ApiPathConfig[];
    /** How to extract the list from the SSE payload (defaults to payload.files) */
    extractFiles?: (parsed: any) => string[];
    /** Debounce to coalesce bursts */
    debounceMs?: number;
};

type UseServerSentEventsOptions = {
    fileInvalidation?: FileInvalidationConfig;
    preserveOnDisconnect?: boolean;
    enabled?: boolean;
    endpointPath?: string;
};

const useServerSentEvents = (
    subscriptions: Partial<Record<EventType, (e: MessageEvent) => void>>,
    options: UseServerSentEventsOptions = {},
) => {
    const subsRef = useRef(subscriptions);
    useEffect(() => {
        subsRef.current = subscriptions;
    }, [subscriptions]);

    const {
        fileInvalidation,
        preserveOnDisconnect = false,
        enabled = true,
        endpointPath,
    } = options;
    const isMemberAuthorized = useIsMemberAuthorized();

    const pendingRef = useRef<Set<string>>(new Set());

    const flushInvalidations = useMemo(() => {
        const wait = fileInvalidation?.debounceMs ?? 800;
        // eslint-disable-next-line react-hooks/refs
        return debounce(() => {
            if (!fileInvalidation) return;

            if (fileInvalidation.urls?.length) {
                for (const url of fileInvalidation.urls) {
                    invalidateFetcherJsonByUrl(url);
                }
            }

            const filenames = Array.from(pendingRef.current);
            pendingRef.current.clear();

            if (isMemberAuthorized && fileInvalidation.apiPaths?.length) {
                for (const apiPathConfig of fileInvalidation.apiPaths) {
                    const shouldInvalidate = filenames.some((filename) =>
                        apiPathConfig.filePatterns.some((pattern) => pattern.test(filename)),
                    );
                    if (shouldInvalidate) {
                        invalidateAllFetcherApiByPath(apiPathConfig.path);
                    }
                }
            }

            const whitelist = new Set(fileInvalidation.filenames ?? []);
            const patterns = fileInvalidation.patterns ?? [];

            for (const item of filenames) {
                if (/^https?:\/\//i.test(item)) {
                    invalidateFetcherJsonByUrl(item);
                    continue;
                }
                const isWhitelisted = whitelist.size ? whitelist.has(item) : true;
                const regexHit = patterns.length ? patterns.some((re) => re.test(item)) : true;

                if (isWhitelisted && regexHit) {
                    invalidateFetcherJsonByFilename(item);
                }
            }
        }, wait);
    }, [fileInvalidation, isMemberAuthorized]);

    useEffect(() => () => flushInvalidations.cancel(), [flushInvalidations]);

    const autoFileSavedHandler = useCallback(
        (event: MessageEvent) => {
            if (!fileInvalidation || !event?.data) return;
            try {
                const parsed = JSON.parse(event.data);
                const extract =
                    fileInvalidation.extractFiles ??
                    ((p: any) => {
                        if (!Array.isArray(p?.files)) return [];
                        // Normalize files: handle both strings and objects with path property
                        return p.files
                            .map((file: any) => (typeof file === 'string' ? file : file.path || ''))
                            .filter(Boolean);
                    });
                const list: string[] = extract(parsed);
                if (!list?.length) return;
                for (const name of list) pendingRef.current.add(name);
                flushInvalidations();
            } catch {
                return;
            }
        },
        [fileInvalidation, flushInvalidations],
    );

    useEffect(() => {
        if (!enabled) return;

        serverEvents.connect(isMemberAuthorized, endpointPath);

        const subscriptionTypes = Object.keys(subsRef.current).toSorted() as EventType[];
        const eventTypes = fileInvalidation
            ? Array.from(new Set<EventType>([...subscriptionTypes, 'fileSaved']))
            : subscriptionTypes;

        for (const type of eventTypes) {
            serverEvents.subscribe(type, (e: MessageEvent) => {
                if (type === 'fileSaved' && fileInvalidation) autoFileSavedHandler(e);
                const cb = subsRef.current[type];
                if (cb) cb(e);
            });
        }

        return () => {
            serverEvents.disconnect({ preserveAuth: preserveOnDisconnect, preserveSubs: false });
        };
    }, [enabled, endpointPath, isMemberAuthorized]);

    return serverEvents;
};

export default useServerSentEvents;
